/**
 * Pure helpers for the Intertown trip receipt (pages/IntertownTripDetails.js), kept out of
 * the component so the rules can be unit-tested.
 */
import { toMillis } from './rideData.js';
import { tidyPlace } from './intertownDisplay.js';

const positive = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * What the receipt shows for an Intertown request document:
 *  - `snapshot`: the fare in the shape `fareRows` reads (null when no fare was recorded)
 *  - `pricingMode` ('fixed' | 'per_km' | null) and `perKm` (per-km trips only)
 *  - `billedKm`: the distance the fare was worked out on (a return counts both ways)
 *  - `durationMin`: minutes from the start of the trip to its end (a return includes the wait)
 * The request holds no payment record, so nothing here says whether the fare was paid.
 */
export const intertownReceipt = (request) => {
  const isReturn = request?.tripType === 'return';
  const baseFare = positive(request?.baseFare);
  const total = positive(request?.totalFare) ?? baseFare;
  const pricingMode = request?.pricingMode === 'per_km' || request?.pricingMode === 'fixed' ? request.pricingMode : null;
  const routeKm = positive(request?.route?.distanceKm);
  const billedKm = positive(request?.estimatedDistanceKm)
    ?? (routeKm ? Math.round(routeKm * (isReturn ? 2 : 1) * 10) / 10 : null);
  const startedAt = toMillis(request?.startedAt);
  const completedAt = toMillis(request?.completedAt);
  const durationMin = request?.status === 'completed' && startedAt && completedAt > startedAt
    ? Math.max(1, Math.round((completedAt - startedAt) / 60000))
    : null;
  return {
    tripType: isReturn ? 'return' : 'one_way',
    total,
    snapshot: total ? {
      total,
      baseFare,
      waitingCharge: positive(request?.waitingCharge) ?? 0,
      waitingMinutes: positive(request?.waitingMinutes) ?? 0,
    } : null,
    pricingMode,
    perKm: pricingMode === 'per_km' ? positive(request?.perKm) : null,
    billedKm,
    durationMin,
  };
};

// ── Book this route again ───────────────────────────────────────────────────

// A stored coordinate is a number. (Number(null) is 0, a real-looking place off the coast of
// Africa, so null and blanks are rejected before any conversion.)
const coordinate = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value);
  return null;
};

/** A place the booking page can use as is: the fields the server reads, readable text, real coordinates. */
const rebookPlace = (place) => {
  const tidy = tidyPlace(place);
  const lat = coordinate(tidy?.lat);
  const lng = coordinate(tidy?.lng);
  const name = String(tidy?.name || tidy?.address || '').trim();
  const address = String(tidy?.address || tidy?.name || '').trim();
  return lat === null || lng === null || !name || !address ? null : { name, address, lat, lng };
};

/**
 * What "Book this route again" hands to the Intertown booking page: the same route (a fixed
 * route's id, or a custom trip's own drop pin), the same trip type, and the old pickup, which
 * the rider can still change there. A popular-route trip whose drop was the rider's own spot
 * (`destinationExact`) hands over that spot too. Null when the trip cannot be repeated: a fixed
 * route with no id, or a custom trip whose drop pin was not recorded. A missing pickup is
 * allowed (the rider simply picks one again).
 */
export const intertownRebook = (request) => {
  if (!request || typeof request !== 'object') return null;
  const tripType = request.tripType === 'return' ? 'return' : 'one_way';
  const pickup = rebookPlace(request.pickup);
  const custom = request.routeKind === 'custom' || (!request.routeKind && request.routeId === 'custom');
  if (custom) {
    const destination = rebookPlace(request.destination);
    return destination ? { kind: 'custom', tripType, pickup, destination } : null;
  }
  const routeId = typeof request.routeId === 'string' ? request.routeId.trim() : '';
  if (!routeId || routeId === 'custom') return null;
  const spot = request.destinationExact === true ? rebookPlace(request.destination) : null;
  return { kind: 'fixed', routeId, tripType, pickup, ...(spot ? { destination: spot } : {}) };
};

// ── Trip timeline ───────────────────────────────────────────────────────────

const CANCELLED_LABEL = {
  passenger: 'intertown.timeline.cancelledByYou',
  admin: 'intertown.timeline.cancelledByAdmin',
  system: 'intertown.timeline.cancelledNoAnna',
};

/** Every string a timeline can show (the tests check each exists in English and Telugu). */
export const TIMELINE_LABEL_KEYS = [
  'intertown.timeline.title',
  'intertown.timeline.requested',
  'intertown.timeline.accepted',
  'intertown.timeline.arrivedPickup',
  'intertown.timeline.started',
  'intertown.timeline.reachedDestination',
  'intertown.timeline.reachedAndCompleted',
  'intertown.timeline.returnStarted',
  'intertown.timeline.waited',
  'intertown.timeline.completed',
  'intertown.timeline.cancelledByYou',
  'intertown.timeline.cancelledByAdmin',
  'intertown.timeline.cancelledNoAnna',
  'intertown.timeline.cancelled',
];

/**
 * The trip's milestones, oldest first, from the times the request records. A step whose time
 * was never recorded is left out. Each step is
 * `{ id, at (ms), tone: 'good' | 'bad', labelKey, detailKey?, detailVars? }`.
 *
 * A one-way trip ends the moment the Anna marks arrival, so reaching the destination and
 * completing are one step. A return trip has its own steps for arriving, setting off back
 * (with the minutes waited, the same figure the fare uses) and finishing. Who cancelled is
 * shown, but never the reason text: an operator's note is internal.
 */
export const intertownTimeline = (request) => {
  const isReturn = request?.tripType === 'return';
  const at = (value) => {
    const ms = toMillis(value);
    return Number.isFinite(ms) && ms > 0 ? ms : 0;
  };
  const arrivedAt = at(request?.arrivedAt) || at(request?.waitingStartedAtMs);
  const completedAt = at(request?.completedAt);
  const waited = positive(request?.waitingMinutes);
  const steps = [];
  const add = (id, when, labelKey, extra = {}) => {
    if (when > 0) steps.push({ id, at: when, tone: 'good', labelKey, ...extra });
  };
  add('requested', at(request?.createdAt), 'intertown.timeline.requested');
  add('accepted', at(request?.acceptedAt), 'intertown.timeline.accepted');
  add('arrivedPickup', at(request?.arrivedAtPickup), 'intertown.timeline.arrivedPickup');
  add('started', at(request?.startedAt), 'intertown.timeline.started');
  if (isReturn) {
    add('reachedDestination', arrivedAt, 'intertown.timeline.reachedDestination');
    add('returnStarted', at(request?.waitingEndedAt), 'intertown.timeline.returnStarted',
      waited ? { detailKey: 'intertown.timeline.waited', detailVars: { min: waited } } : {});
    add('completed', completedAt, 'intertown.timeline.completed');
  } else if (completedAt) {
    add('completed', completedAt, arrivedAt ? 'intertown.timeline.reachedAndCompleted' : 'intertown.timeline.completed');
  } else {
    add('reachedDestination', arrivedAt, 'intertown.timeline.reachedDestination');
  }
  const cancelledAt = at(request?.cancelledAt);
  if (cancelledAt > 0) {
    steps.push({
      id: 'cancelled', at: cancelledAt, tone: 'bad', labelKey: Object.prototype.hasOwnProperty.call(CANCELLED_LABEL, request?.cancelledBy) ? CANCELLED_LABEL[request.cancelledBy] : 'intertown.timeline.cancelled',
    });
  }
  return steps.sort((a, b) => a.at - b.at);
};

/**
 * The time to print beside each step, and a date only where it is needed: on the first step
 * and whenever the day changes, so a trip that runs past midnight is not misread.
 */
export const timelineTimeLabels = (steps, { locale = 'en-IN', timeZone } = {}) => {
  const timeFormat = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hour12: true, timeZone });
  const dayFormat = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone });
  let lastDay = null;
  return steps.map((step) => {
    const when = new Date(step.at);
    const day = dayFormat.format(when);
    const label = { time: timeFormat.format(when), date: day === lastDay ? null : day };
    lastDay = day;
    return label;
  });
};
